import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  X,
  Mail,
  Lock,
  User as UserIcon,
  UserPlus,
  Send,
  CheckCircle2,
  Copy,
  Pencil,
  Trash2,
  Users,
  AlertTriangle,
  Rocket,
  Loader2,
  Plus,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { usersApi, invitesApi, permissionsApi, billingApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";
import RoleButtons, { ROLE_LABEL } from "./RoleButtons";
import EmbeddedStripeCheckoutModal from "./payments/EmbeddedStripeCheckoutModal";

// Shared role badge palette (used on Staff cards, invite rows, account modal).
export const ROLE_BADGE = {
  admin: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  dispatcher: "bg-blue-50 text-blue-700 border-blue-200",
  technician: "bg-amber-50 text-amber-700 border-amber-200",
  office_personnel: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function PermBadge({ role, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-semibold border ${
        ROLE_BADGE[role] || "bg-slate-50 text-slate-700 border-slate-200"
      }`}
    >
      {ROLE_LABEL[role] || role}
    </span>
  );
}

export function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        {children}
      </div>
    </div>
  );
}

export function CopyableLink({ url, testid }) {
  const [copied, setCopied] = useState(false);
  if (!url) return <span className="text-[11.5px] text-slate-400">—</span>;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={onCopy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 max-w-[280px] px-2 py-1 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-[11.5px] text-slate-700 font-mono truncate"
      title={url}
    >
      {copied ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-slate-500 shrink-0" />
      )}
      <span className="truncate">{url.replace(/^https?:\/\/[^/]+/, "")}</span>
    </button>
  );
}

function ModalShell({ icon: Icon, title, onClose, closeTestid, accent, children, wide }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} border border-slate-200 max-h-[90vh] flex flex-col`}>
        <div className={`px-5 py-3.5 border-b border-slate-100 flex items-center justify-between ${accent || ""}`}>
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">{title}</div>
          </div>
          <button
            onClick={onClose}
            data-testid={closeTestid}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
}

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Load current activation status once — every modal needs it. */
function useActivationGate() {
  const [status, setStatus] = useState(null);
  const reload = useCallback(() => {
    billingApi.activationStatus().then(setStatus).catch(() => setStatus(null));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const trialStatus = status?.trial?.status;
  const inTrial = trialStatus === "trialing";
  const monthlyPrepaid = status?.prepaidActivations?.monthly || 0;
  const annualPrepaid = status?.prepaidActivations?.annual || 0;
  const totalPrepaid = monthlyPrepaid + annualPrepaid;
  return { status, inTrial, totalPrepaid, reload };
}

/** ================================================================
 *  Multi-row "Add staff member" popup — invite-only.
 *  - Trial: sends all invites immediately, free.
 *  - Post-trial: sums prorated amounts across rows (per-row term), opens
 *    Stripe Embedded Checkout inline; on payment success, auto-fires all
 *    invites and shows a copy-link result screen for each.
 *  ================================================================ */
const EMPTY_ROW = () => ({ name: "", email: "", role: "dispatcher", term: "monthly" });

export function CreateUserModal({ onClose, onCreated, initialValues, onSent }) {
  const { status, inTrial, totalPrepaid, reload } = useActivationGate();
  const [rows, setRows] = useState(() => {
    if (initialValues?.email) {
      return [{ name: initialValues.name || "", email: initialValues.email || "",
                role: initialValues.role || "dispatcher", term: "monthly" }];
    }
    return [EMPTY_ROW()];
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null); // {clientSecret, sessionId, amount}
  const [results, setResults] = useState(null);   // {sent: [inv, inv, ...]}

  const setRow = (i, patch) => setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((cur) => [...cur, EMPTY_ROW()]);
  const removeRow = (i) => setRows((cur) => (cur.length > 1 ? cur.filter((_, idx) => idx !== i) : cur));

  const prorate = status?.prorate || {};
  const perTermPrice = {
    monthly: Number(prorate?.monthly?.perUserProrated || 0),
    annual: Number(prorate?.annual?.perUserProrated || 0),
  };

  // Total ONLY applies post-trial. During trial + when we have prepaid credits,
  // rows are covered by those credits first, then billed.
  const rowsPaid = useMemo(() => {
    if (inTrial) return { billableRows: [], freeRows: rows, coveredByCredits: 0 };
    // Best-effort: assign prepaid credits by term (annual first, then monthly).
    let mCredits = status?.prepaidActivations?.monthly || 0;
    let aCredits = status?.prepaidActivations?.annual || 0;
    const covered = [];
    const billable = [];
    for (const r of rows) {
      if (r.term === "annual" && aCredits > 0) {
        aCredits -= 1; covered.push(r);
      } else if (r.term === "monthly" && mCredits > 0) {
        mCredits -= 1; covered.push(r);
      } else {
        billable.push(r);
      }
    }
    return { billableRows: billable, freeRows: covered, coveredByCredits: covered.length };
  }, [rows, inTrial, status]);

  const totalDue = useMemo(() =>
    rowsPaid.billableRows.reduce((s, r) => s + (perTermPrice[r.term] || 0), 0),
  [rowsPaid.billableRows, perTermPrice]);

  const validate = () => {
    const bad = [];
    rows.forEach((r, i) => {
      const email = (r.email || "").trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        bad.push(`Row ${i + 1}: enter a valid email address`);
      }
    });
    return bad;
  };

  // Fire N invites in sequence, collecting results. Any 402 in the middle is
  // caught and surfaced to the admin.
  const sendAllInvites = useCallback(async () => {
    const sent = [];
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      const body = {
        email: r.email.trim().toLowerCase(),
        role: r.role,
        name: (r.name || "").trim() || undefined,
      };
      const inv = await invitesApi.create(body);
      sent.push(inv);
    }
    return sent;
  }, [rows]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const bad = validate();
    if (bad.length) { setError(bad.join(" · ")); return; }

    setBusy(true);
    try {
      // If everything is either free-trial or covered by prepaid credits — send now.
      if (rowsPaid.billableRows.length === 0) {
        const sent = await sendAllInvites();
        setResults({ sent });
        onSent?.(sent);
        return;
      }
      // Otherwise: open embedded checkout for the total, then invite on success.
      const body = {
        term: "monthly", // ignored when prefills[].term is set per row
        origin_url: window.location.origin,
        modal_kind: "invite",
        embedded: true,
        prefills: rows.map((r) => ({
          email: r.email.trim().toLowerCase(),
          name: (r.name || "").trim() || null,
          role: r.role,
          term: r.term,
        })),
      };
      const resp = await billingApi.prepayNewUser(body);
      if (!resp?.client_secret) {
        setError("Stripe returned no client_secret — try again");
        return;
      }
      setCheckout({
        clientSecret: resp.client_secret,
        sessionId: resp.session_id,
        amount: resp.amount,
        seatCount: resp.seat_count,
      });
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  const onPaymentComplete = useCallback(async () => {
    if (!checkout?.sessionId) return;
    // Poll until backend confirms `paid` and the prepaid credits are minted.
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const res = await billingApi.prepayStatus(checkout.sessionId).catch(() => null);
      if (res?.payment_status === "paid") {
        toast.success("Payment received — sending invitations…");
        setCheckout(null);
        reload();
        try {
          const sent = await sendAllInvites();
          setResults({ sent });
          onSent?.(sent);
        } catch (err) {
          setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing — invites will send once it clears");
    setCheckout(null);
  }, [checkout, sendAllInvites, reload, onSent]);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (_) { toast.error("Copy failed"); }
  };

  /** ================== Render ================== */

  // Success screen — one card per invitation with the magic link.
  if (results) {
    const sent = results.sent || [];
    return (
      <ModalShell icon={UserPlus} title={sent.length === 1 ? "Invitation sent" : `${sent.length} invitations sent`} onClose={() => { onCreated?.(); onClose(); }} closeTestid="create-user-close" wide>
        <div className="p-5 space-y-3.5" data-testid="create-user-invite-result">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            <div className="text-[14px] font-semibold">All invitations created</div>
          </div>
          <div className="text-[12.5px] text-slate-600">
            Each teammate has 72 hours to click their magic link and set a password. Copy any link below if you&apos;d rather share it manually.
          </div>
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {sent.map((inv) => (
              <div key={inv.id} className="p-3 flex items-center justify-between gap-3" data-testid={`create-user-invite-row-${inv.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{inv.email}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    <PermBadge role={inv.role} /> {inv.email_sent ? "· email delivered" : "· email pending"}
                  </div>
                </div>
                <Button onClick={() => copyLink(inv.accept_url)} data-testid={`create-user-invite-copy-${inv.id}`} className="h-8 px-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11.5px]">
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end pt-1">
            <Button onClick={() => { onCreated?.(); onClose(); }} data-testid="create-user-invite-close" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">Done</Button>
          </div>
        </div>
      </ModalShell>
    );
  }

  return (
    <>
      <ModalShell icon={UserPlus} title="Add staff member" onClose={onClose} closeTestid="create-user-close" wide>
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="create-user-form">
          {/* Trial banner */}
          {inTrial && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border text-[12px] bg-amber-50/60 border-amber-200 text-amber-800" data-testid="staff-modal-trial-banner">
              <Users className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>Free trial</strong> — invitations go out immediately at no cost
                {status?.trial?.daysLeft != null && ` (${status.trial.daysLeft} day${status.trial.daysLeft === 1 ? "" : "s"} left)`}.
              </span>
            </div>
          )}
          {!inTrial && totalPrepaid > 0 && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border text-[12px] bg-emerald-50/70 border-emerald-200 text-emerald-800" data-testid="staff-modal-prepaid-banner">
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>{totalPrepaid}</strong> prepaid activation{totalPrepaid === 1 ? "" : "s"} available — the first {totalPrepaid} row{totalPrepaid === 1 ? "" : "s"} below invite for free.
              </span>
            </div>
          )}

          {/* Row list */}
          <div className="space-y-2.5" data-testid="create-user-rows">
            {rows.map((r, i) => {
              const covered = rowsPaid.freeRows.includes(r);
              const priceForRow = inTrial || covered ? 0 : perTermPrice[r.term] || 0;
              return (
                <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/40 p-3" data-testid={`create-user-row-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_auto] gap-2.5 items-start">
                    <Field label={`Name (row ${i + 1})`} icon={UserIcon}>
                      <input
                        value={r.name}
                        onChange={(e) => setRow(i, { name: e.target.value })}
                        data-testid={`create-user-name-${i}`}
                        placeholder="Full name"
                        className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      />
                    </Field>
                    <Field label="Email" icon={Mail}>
                      <input
                        type="email"
                        required
                        value={r.email}
                        onChange={(e) => setRow(i, { email: e.target.value })}
                        data-testid={`create-user-email-${i}`}
                        placeholder="teammate@company.com"
                        className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      />
                    </Field>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        data-testid={`create-user-row-remove-${i}`}
                        className="h-10 w-9 mt-[22px] rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50 flex items-center justify-center"
                        title="Remove row"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-2 items-end">
                    <div>
                      <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Permission</label>
                      <RoleButtons value={r.role} onChange={(v) => setRow(i, { role: v })} testidPrefix={`create-user-role-${i}`} />
                    </div>
                    {!inTrial && (
                      <div className="text-right">
                        <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 bg-white">
                          {["monthly", "annual"].map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setRow(i, { term: t })}
                              data-testid={`create-user-row-term-${i}-${t}`}
                              className={`px-3 py-1.5 text-[11.5px] font-semibold ${
                                r.term === t ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {t === "monthly" ? "Monthly" : "Annual"}
                            </button>
                          ))}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 tabular-nums" data-testid={`create-user-row-price-${i}`}>
                          {covered ? (
                            <span className="text-emerald-600 font-semibold">Covered by prepaid credit</span>
                          ) : (
                            <>Prorated · <strong>{money(priceForRow)}</strong></>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add-another */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addRow}
              data-testid="create-user-add-row"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/40 text-[12.5px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" /> Add another staff member
            </button>
            <div className="text-[11.5px] text-slate-500">
              {rows.length} member{rows.length === 1 ? "" : "s"} · no password field — each teammate picks their own
            </div>
          </div>

          {/* Total footer */}
          {!inTrial && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-between" data-testid="create-user-total-row">
              <div className="text-[12px] text-slate-600">
                {rowsPaid.coveredByCredits > 0 && (
                  <div className="text-[11px] text-emerald-700">
                    {rowsPaid.coveredByCredits} row{rowsPaid.coveredByCredits === 1 ? "" : "s"} covered by prepaid credits (free)
                  </div>
                )}
                <div>
                  Charging <strong>{rowsPaid.billableRows.length}</strong> new seat{rowsPaid.billableRows.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Total due today</div>
                <div className="text-[22px] font-extrabold text-slate-900 tabular-nums" data-testid="create-user-total-amount">
                  {money(totalDue)}
                </div>
              </div>
            </div>
          )}

          {/* Manage-many hint */}
          {!inTrial && totalDue > 0 && (
            <div className="flex items-start gap-1.5 text-[11.5px] text-slate-500" data-testid="create-user-manage-hint">
              <Info className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span>
                Prefer to activate existing trial users instead?
                {" "}<Link to="/settings/billing" className="font-semibold text-blue-600 hover:underline">Open Billing</Link> to activate several at once.
              </span>
            </div>
          )}

          {error && (
            <div data-testid="create-user-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            {inTrial || totalDue === 0 ? (
              <Button type="submit" disabled={busy} data-testid="create-user-submit" className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13.5px] font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                {busy ? "Sending…" : rows.length === 1 ? "Send invitation" : `Send ${rows.length} invitations`}
              </Button>
            ) : (
              <Button type="submit" disabled={busy} data-testid="create-user-pay-and-invite" className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-[13.5px] font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5" />}
                Pay {money(totalDue)} &amp; send {rows.length === 1 ? "invitation" : `${rows.length} invitations`}
              </Button>
            )}
          </div>
        </form>
      </ModalShell>

      {checkout && (
        <EmbeddedStripeCheckoutModal
          clientSecret={checkout.clientSecret}
          title={`Activate ${checkout.seatCount} seat${checkout.seatCount === 1 ? "" : "s"}`}
          subtitle={`Total ${money(checkout.amount)} · invites send automatically after payment`}
          onComplete={onPaymentComplete}
          onClose={() => setCheckout(null)}
          testid="staff-embedded-checkout"
        />
      )}
    </>
  );
}

/** Legacy single-invite modal (kept as a thin shim). The new multi-row
 *  `CreateUserModal` covers both create-and-invite from one popup, but a few
 *  return-from-Stripe URL paths on the Technicians page still open this modal
 *  with a single prefilled row. We just delegate to the new modal internally. */
export function InviteModal({ onClose, onSent, initialValues }) {
  return (
    <CreateUserModal
      initialValues={initialValues}
      onClose={onClose}
      onCreated={onClose}
      onSent={(sent) => {
        if (Array.isArray(sent)) sent.forEach((s) => onSent?.(s));
        else onSent?.(sent);
      }}
    />
  );
}

/** Manage an existing login account: edit name / permission / password, or delete. */
export function AccountModal({ account, isSelf, onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(account.name || "");
  const initialTypeId = account.permissionTypeId || account.role;
  const [permTypeId, setPermTypeId] = useState(initialTypeId);
  const [types, setTypes] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    permissionsApi.types().then(setTypes).catch(() => setTypes([]));
  }, []);

  const selectedType = types.find((t) => t.id === permTypeId);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isSelf && selectedType && selectedType.baseRole !== "admin") {
      setError("You can't remove your own admin access — it would lock you out.");
      return;
    }
    const patch = {};
    if (name.trim() && name !== account.name) patch.name = name.trim();
    if (permTypeId !== initialTypeId) patch.permissionTypeId = permTypeId || null;
    if (newPassword) patch.password = newPassword;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const updated = await usersApi.update(account.id, patch);
      onSaved && onSaved(updated);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete account "${account.email}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await usersApi.remove(account.id);
      onDeleted && onDeleted(account);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  return (
    <ModalShell icon={Pencil} title={`Manage ${account.email}`} onClose={onClose} closeTestid="account-modal-close">
      <form onSubmit={submit} className="p-5 space-y-3.5" data-testid="account-form">
        <Field label="Name" icon={UserIcon}>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
        </Field>
        <div>
          <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Permission Type</label>
          <select
            value={permTypeId}
            onChange={(e) => setPermTypeId(e.target.value)}
            data-testid="account-permission-type"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.system ? "" : ` (custom · ${t.baseRole.replace("_", " ")})`}
              </option>
            ))}
          </select>
          <div className="text-[10.5px] text-slate-400 mt-1">Controls which areas this user can access. Manage the list under Company Settings → Permission Types.</div>
          {isSelf && (
            <div className="text-[10.5px] text-amber-600 mt-1">
              You can&apos;t remove your own admin access — would lock you out of admin tools.
            </div>
          )}
        </div>
        <Field label="New password (optional)" icon={Lock}>
          <input type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="account-password" placeholder="Leave blank to keep current" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
        </Field>
        {error && <div data-testid="account-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">{error}</div>}
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            disabled={isSelf || deleting}
            onClick={onDelete}
            data-testid="account-delete"
            className="h-9 text-[13px] text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> {deleting ? "Deleting…" : "Delete"}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="account-submit" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}
